import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Button, Chip,
  CircularProgress, Alert, LinearProgress,
} from '@mui/material';
import { Coffee, CardGiftcard, Apple, PhoneAndroid, LocationOn, History } from '@mui/icons-material';
import loyaltyService, { CardDetail } from '../../services/loyaltyService';
import { colors, designTokens } from '../../theme/theme';
import { GlassCard } from '../../components/shared';
import AppSnackbar from '../../components/shared/AppSnackbar';

export default function LoyaltyCardDetailPage() {
  const { cardId } = useParams<{ cardId: string }>();
  const navigate = useNavigate();
  const [card, setCard] = useState<CardDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [redeemingId, setRedeemingId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'error' | 'success' | 'info' | 'warning' }>(
    { open: false, message: '', severity: 'info' }
  );

  useEffect(() => {
    if (!cardId) return;
    loyaltyService.getCardDetail(cardId)
      .then(setCard)
      .catch(() => { /* no-op */ })
      .finally(() => setLoading(false));
  }, [cardId]);

  const handleRedeem = async (rewardId: string) => {
    setRedeemingId(rewardId);
    try {
      await loyaltyService.redeemReward(rewardId);
      // Refresh card
      if (cardId) {
        const updated = await loyaltyService.getCardDetail(cardId);
        setCard(updated);
      }
    } catch (e: any) {
      console.warn('[LoyaltyCardDetailPage] redeem reward failed', e);
      setSnackbar({ open: true, message: e.message || 'Redemption failed', severity: 'error' });
    } finally {
      setRedeemingId(null);
    }
  };

  if (loading) return <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}><CircularProgress sx={{ color: colors.primary.main }} /></Container>;
  if (!card) return <Container maxWidth="sm" sx={{ py: 4 }}><Alert severity="error">Card not found</Alert></Container>;

  const progress = (card.stamps_count / card.stamps_required) * 100;
  const earnedRewards = card.rewards.filter(r => r.status === 'earned');
  const pastRewards = card.rewards.filter(r => r.status !== 'earned');

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      {/* Venue Header */}
      {card.venue.photo && (
        <Box component="img" src={card.venue.photo} alt={card.venue.name}
          sx={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: `${designTokens.radius.lg}px`, mb: 2, border: '1px solid rgba(255,255,255,0.06)' }} />
      )}
      <Typography variant="h5" sx={{ fontWeight: 800, color: '#FFFFFF', mb: 0.5 }}>
        {card.venue.name}
      </Typography>
      {card.venue.address && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2, color: colors.neutral[500] }}>
          <LocationOn fontSize="small" />
          <Typography variant="body2" sx={{ color: colors.neutral[500] }}>{card.venue.address}</Typography>
        </Box>
      )}

      {/* Progress Card — dark glass with subtle coral border (1px, not 2px) */}
      <GlassCard
        padding={3}
        sx={{
          mb: 3,
          borderColor: `${colors.primary.main}33`,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h2" sx={{ fontWeight: 900, color: colors.primary.main, lineHeight: 1 }}>
            {card.stamps_count}
            <Typography component="span" variant="h4" sx={{ color: colors.neutral[500], fontWeight: 700 }}>
              /{card.stamps_required}
            </Typography>
          </Typography>
          <Typography variant="body2" sx={{ color: colors.neutral[500], mt: 1 }}>
            {card.stamps_required - card.stamps_count > 0
              ? `${card.stamps_required - card.stamps_count} more for ${card.program.reward_description}`
              : `${card.program.reward_description} earned!`}
          </Typography>
        </Box>

        <LinearProgress variant="determinate" value={progress}
          sx={{ height: 14, borderRadius: 7, bgcolor: 'rgba(255,255,255,0.06)',
            '& .MuiLinearProgress-bar': { bgcolor: colors.primary.main, borderRadius: 7 } }} />

        {/* Stamp circles */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2.5, flexWrap: 'wrap' }}>
          {Array.from({ length: card.stamps_required }).map((_, i) => {
            const filled = i < card.stamps_count;
            return (
              <Box key={i} sx={{
                width: 36, height: 36, borderRadius: '50%',
                bgcolor: filled ? colors.primary.main : 'rgba(255,255,255,0.04)',
                border: `1px solid ${filled ? colors.primary.main : 'rgba(255,255,255,0.12)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {filled && <Coffee sx={{ fontSize: 16, color: '#FFFFFF' }} />}
              </Box>
            );
          })}
        </Box>

        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1.5, color: colors.neutral[500] }}>
          {card.total_stamps} total stamps · {card.rewards_earned} rewards earned
        </Typography>
      </GlassCard>

      {/* Wallet Buttons — outlined dark style */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Apple />}
          onClick={() => setSnackbar({ open: true, message: 'Apple Wallet: configure your Apple Developer certificate first', severity: 'info' })}
          sx={{
            borderColor: 'rgba(255,255,255,0.12)',
            color: '#FFFFFF',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { borderColor: 'rgba(255,255,255,0.25)', bgcolor: 'rgba(255,255,255,0.04)' },
          }}
        >
          Apple Wallet
        </Button>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<PhoneAndroid />}
          onClick={() => setSnackbar({ open: true, message: 'Google Wallet: configure your Google Cloud service account first', severity: 'info' })}
          sx={{
            borderColor: 'rgba(255,255,255,0.12)',
            color: '#FFFFFF',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { borderColor: 'rgba(255,255,255,0.25)', bgcolor: 'rgba(255,255,255,0.04)' },
          }}
        >
          Google Wallet
        </Button>
      </Box>

      {/* Active Rewards */}
      {earnedRewards.length > 0 && (
        <GlassCard
          padding={2.5}
          sx={{
            mb: 3,
            borderColor: `${colors.accent.main}40`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CardGiftcard sx={{ color: colors.accent.main, fontSize: 22 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
              {earnedRewards.length} Reward{earnedRewards.length > 1 ? 's' : ''} Available
            </Typography>
          </Box>
          {earnedRewards.map(r => (
            <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, '&:last-child': { mb: 0 } }}>
              <CardGiftcard sx={{ color: colors.accent.main }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#FFFFFF' }}>{r.description}</Typography>
                {r.qr_token && (
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: colors.neutral[500] }}>
                    Code: {r.qr_token.substring(0, 8).toUpperCase()}
                  </Typography>
                )}
                {r.expires_at && (
                  <Typography variant="caption" display="block" sx={{ color: colors.neutral[500] }}>
                    Expires: {new Date(r.expires_at).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
              <Button variant="contained" size="small"
                disabled={redeemingId === r.id}
                onClick={() => handleRedeem(r.id)}
                sx={{ bgcolor: colors.accent.main, color: '#0F0F13', textTransform: 'none', fontWeight: 700, boxShadow: 'none', '&:hover': { bgcolor: colors.accent.light, boxShadow: '0 0 16px rgba(251,191,36,0.3)' } }}>
                {redeemingId === r.id ? '...' : 'Use'}
              </Button>
            </Box>
          ))}
        </GlassCard>
      )}

      {/* Stamp History */}
      <GlassCard padding={2.5} sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <History sx={{ color: colors.primary.main }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF' }}>Stamp History</Typography>
        </Box>
        {card.stamp_history.length === 0 ? (
          <Typography variant="body2" sx={{ color: colors.neutral[500] }}>No stamps yet. Scan the QR at the counter to earn your first.</Typography>
        ) : (
          card.stamp_history.map((s, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.25,
              borderBottom: i < card.stamp_history.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: '50%',
                bgcolor: s.is_reward_stamp ? colors.accent.main : colors.primary.main,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {s.is_reward_stamp
                  ? <CardGiftcard sx={{ fontSize: 14, color: '#0F0F13' }} />
                  : <Coffee sx={{ fontSize: 14, color: '#FFFFFF' }} />}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                  Stamp #{s.stamp_number} {s.is_reward_stamp ? '— Reward earned!' : ''}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.neutral[500] }}>
                  {new Date(s.created_at).toLocaleDateString()} · {s.source.replace('_', ' ')}
                </Typography>
              </Box>
            </Box>
          ))
        )}
      </GlassCard>

      {/* Past Rewards */}
      {pastRewards.length > 0 && (
        <GlassCard padding={2.5} sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#FFFFFF' }}>Past Rewards</Typography>
          {pastRewards.map(r => (
            <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
              <Chip label={r.status} size="small"
                sx={{
                  bgcolor: r.status === 'redeemed' ? colors.success.subtle : colors.error.subtle,
                  color: r.status === 'redeemed' ? colors.success.main : colors.error.main,
                  fontWeight: 700,
                  border: `1px solid ${r.status === 'redeemed' ? colors.success.main : colors.error.main}33`,
                }}
              />
              <Typography variant="body2" sx={{ color: '#FFFFFF' }}>{r.description}</Typography>
              <Typography variant="caption" sx={{ color: colors.neutral[500], ml: 'auto' }}>
                {r.redeemed_at ? new Date(r.redeemed_at).toLocaleDateString() : ''}
              </Typography>
            </Box>
          ))}
        </GlassCard>
      )}

      {/* Venue link */}
      {card.venue.slug && (
        <Button fullWidth variant="text" onClick={() => navigate(`/venues/${card.venue.slug}`)}
          sx={{ color: colors.primary.main }}>
          View {card.venue.name} Page →
        </Button>
      )}

      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      />
    </Container>
  );
}
